import {
  Kap_Measure,
  Kap_Measure_Annual_Report,
  Maybe,
  Project_Goal,
  Project_Goal_Annual_Report,
  Project_Goal_Feature,
  Project_Measure,
  Project_Measure_Annual_Report,
} from 'src/@types/graphql'
import { PROJECT_TYPE } from 'src/shared/constants/constants'

export const MILESTONE_RESPONSIBLE_TYPE = {
  GFCH: 'GFCH',
  PROJECT_ORGANIZATION: 'PROJECT_ORGANIZATION',
} as const

export type MILESTONE_RESPONSIBLE_TYPE_TYPE = keyof typeof MILESTONE_RESPONSIBLE_TYPE

export const MILESTONE_CONFIG_FREQUENCY = {
  YEARLY: 'YEARLY',
  ONCE: 'ONCE',
} as const

export type MILESTONE_CONFIG_FREQUENCY_TYPE = keyof typeof MILESTONE_CONFIG_FREQUENCY

export const MILESTONE_CONFIG_WORKFLOW = {
  SIMPLE: 'SIMPLE',
  DEFAULT: 'DEFAULT',
} as const

export type MILESTONE_CONFIG_WORKFLOW_TYPE = keyof typeof MILESTONE_CONFIG_WORKFLOW

export const MILESTONE_TYPE = {
  ANNUAL_PLAN: 'ANNUAL_PLAN',
  ANNUAL_REPORT: 'ANNUAL_REPORT',
  CONTRACT: 'CONTRACT',
  EVALUATION_CONCEPT: 'EVALUATION_CONCEPT',
  FINAL_REPORT: 'FINAL_REPORT',
  FINANCIAL_REPORT: 'FINANCIAL_REPORT',
  INTERMEDIATE_DISCUSSION: 'INTERMEDIATE_DISCUSSION',
} as const

export type MILESTONE_TYPE_TYPE = keyof typeof MILESTONE_TYPE

export const MILESTONE_TYPE_FOR_PROCESS: Record<PROJECT_TYPE, MILESTONE_TYPE_TYPE[]> = {
  PF_KAP: [
    MILESTONE_TYPE.ANNUAL_REPORT,
    MILESTONE_TYPE.CONTRACT,
    MILESTONE_TYPE.FINAL_REPORT,
    MILESTONE_TYPE.FINANCIAL_REPORT,
    MILESTONE_TYPE.INTERMEDIATE_DISCUSSION,
  ],
  PF_PGV: [
    MILESTONE_TYPE.ANNUAL_REPORT,
    MILESTONE_TYPE.CONTRACT,
    MILESTONE_TYPE.EVALUATION_CONCEPT,
    MILESTONE_TYPE.FINAL_REPORT,
    MILESTONE_TYPE.FINANCIAL_REPORT,
    MILESTONE_TYPE.INTERMEDIATE_DISCUSSION,
  ],
  KAP: [
    MILESTONE_TYPE.ANNUAL_PLAN,
    MILESTONE_TYPE.ANNUAL_REPORT,
    MILESTONE_TYPE.CONTRACT,
    MILESTONE_TYPE.FINAL_REPORT,
    MILESTONE_TYPE.FINANCIAL_REPORT,
    MILESTONE_TYPE.INTERMEDIATE_DISCUSSION,
  ],
}

export const MILESTONE_STATUS = {
  OPEN: 'OPEN',
  REVIEW: 'REVIEW',
  REWORK: 'REWORK',
  DONE: 'DONE',
} as const

export type MILESTONE_STATUS_TYPE = keyof typeof MILESTONE_STATUS

// Milestone 'statuses after support' used in final report conclusion tab
export const MILESTONE_STATUS_AFTER_SUPPORT = {
  CONTINUING_INNOVATION: 'CONTINUING_INNOVATION',
  CONTINUING_MULTIPLICATION: 'CONTINUING_MULTIPLICATION',
  CHANGE_INNOVATION_TO_MULTIPLICATION: 'CHANGE_INNOVATION_TO_MULTIPLICATION',
  CHANGE_MULTIPLICATION_TO_OFFER_PROMOTION: 'CHANGE_MULTIPLICATION_TO_OFFER_PROMOTION',
  RENEWAL_EXTENSION_OFFER_PROMOTION: 'RENEWAL_EXTENSION_OFFER_PROMOTION',
  CONTINUED_BY_ORGANIZATION: 'CONTINUED_BY_ORGANIZATION',
  SUPPORTED_BY_GFCH: 'SUPPORTED_BY_GFCH',
  COMPLETED_NOT_CONTINUED: 'COMPLETED_NOT_CONTINUED',
} as const

export const ALL_MILESTONE_STATUSES_AFTER_SUPPORT: MILESTONE_STATUS_AFTER_SUPPORT_TYPE[] = Object.keys(
  MILESTONE_STATUS_AFTER_SUPPORT,
) as MILESTONE_STATUS_AFTER_SUPPORT_TYPE[]

export type MILESTONE_STATUS_AFTER_SUPPORT_TYPE = keyof typeof MILESTONE_STATUS_AFTER_SUPPORT

// Milestone statuses for 'cantonal support' used in final report conclusion tab
export const MILESTONE_CANTONAL_SUPPORT = {
  SUPPORTED_IN_CANTONS: 'SUPPORTED_IN_CANTONS',
  SUPPORTED_BY_KAP: 'SUPPORTED_BY_KAP',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
}

export type MILESTONE_CANTONAL_SUPPORT_TYPE = keyof typeof MILESTONE_CANTONAL_SUPPORT

export const ALL_MILESTONE_CANTONAL_SUPPORT: MILESTONE_CANTONAL_SUPPORT_TYPE[] = Object.keys(
  MILESTONE_CANTONAL_SUPPORT,
) as MILESTONE_CANTONAL_SUPPORT_TYPE[]

// Rating conclusion final report milestone
export const CONCLUSION_RATING = {
  GREEN: 'GREEN',
  ORANGE: 'ORANGE',
  RED: 'RED',
} as const

// Goals and measure ratings
export const GOAL_MEASURE_RATING = {
  GREEN: 'GREEN',
  ORANGE: 'ORANGE',
  RED: 'RED',
  INACTIVE: 'INACTIVE',
} as const

export interface MilestoneDetailsValidation {
  requiredNotes: boolean
  requiredDocuments: boolean
}

export interface ProjectGoalAnnualReport {
  goal_id: number
  name: string
  description?: string | null
  rating: string | null
  rating_comment?: string
  goal_measures: undefined | (Project_Measure & Project_Measure_Annual_Report)[]
  sort_number: Maybe<number> | undefined
  main_area_goal_features: undefined | Project_Goal_Feature[]
  second_area_goal_features: undefined | Project_Goal_Feature[]
}

export interface KapGoalAnnualReport {
  goal_id: number
  rating: string | null
  reason?: string
  description?: string | null
  goal_measures?: undefined | (Kap_Measure & Kap_Measure_Annual_Report)[]
}

export interface MeasureAnnualReport {
  measure_id: number
  name: string
  description?: string | null
  rating: string | null
  rating_comment?: string
  goal_measures: undefined | (Project_Goal & Project_Goal_Annual_Report)[]
  sort_number: Maybe<number> | undefined
}

export type KapMeasureAndValid = Kap_Measure & { milestoneMeasureValid: boolean }
